import AppInput from '@nxsys/components/AppInput';
import AppFormContainer from '@nxsys/components/AppFormContainer';
import { AppState } from '@nxsys/store';
import { getFieldLabelByCountry } from '@nxsys/utils/labelConfig';
import { Form } from 'antd';
import React from 'react';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import StepCard from './StepCard';
import { isValidNINumber, isValidPPSNumber } from '@nxsys/utils';
import { IRELAND } from '@nxsys/store/constants/keys';

interface AddNINumberProps {
  stepNumber: number;
  NINumber?: string | null;
  completed?: boolean;
  onSubmitStep: (values: any) => void;
}

type AddNINumberValues = {
  niNumberValue: string;
};

const AddNINumber: React.FC<AddNINumberProps> = ({
  stepNumber,
  NINumber,
  completed = false,
  onSubmitStep,
}) => {
  const [form] = Form.useForm<AddNINumberValues>();
  const { messages } = useIntl();
  const { payrollCountry } = useSelector((state: AppState) => state.appStore);
  const niLabel = getFieldLabelByCountry(payrollCountry ?? '', 'NINo');
  // Ireland collects a PPS number in the same field (label comes from
  // getFieldLabelByCountry) and validates it with the PPS format instead.
  const isIreland = payrollCountry === IRELAND;

  const onFinish = (values: AddNINumberValues) => {
    const params = {
      NINumber: values?.niNumberValue ?? null,
    };
    onSubmitStep(params);
  };

  return (
    <StepCard
      // The field below was already relabelled for Ireland; the step heading
      // above it still read the UK message key, so an Irish worker saw "Add NI
      // Number" over a "PPS Number" input.
      title={
        isIreland
          ? `Add ${niLabel}`
          : (messages['prospect.onboarding.addNINumber'] as string)
      }
      stepNumber={stepNumber}
      completed={completed}
      actions={[
        {
          variant: 'outlined',
          name: messages['common.save'] as string,
          onClick: () => form.submit(),
        },
      ]}
    >
      <AppFormContainer
        form={form}
        onFinish={onFinish}
        initialValues={{
          niNumberValue: NINumber ?? '',
        }}
        wrapperCol={{ span: 24 }}
        layout="vertical"
      >
        <Form.Item
          label={niLabel}
          name="niNumberValue"
          rules={[
            {
              required: true,
              message: messages['form.pleaseEnter'] as string,
            },
            {
              validator: async (_, value) => {
                if (!value) return Promise.resolve();
                const isValid = isIreland
                  ? isValidPPSNumber(value)
                  : isValidNINumber(value);
                if (isValid) return Promise.resolve();
                return Promise.reject(
                  new Error(
                    (isIreland
                      ? messages['form.invalidPPSNumber']
                      : messages['form.invalidNINumber']) as string,
                  ),
                );
              },
            },
          ]}
        >
          <AppInput
            placeholder={`${messages['form.enter'] as string} ${niLabel}`}
            onInput={(e) => {
              const input = e.currentTarget;
              input.value = input.value.toUpperCase().slice(0, 9);
            }}
          />
        </Form.Item>
      </AppFormContainer>
    </StepCard>
  );
};

export default AddNINumber;
